import { debounce } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import {
  Autocomplete,
  FormHelperText,
  IconButton,
  MenuItem,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
} from '@mui/material';

import axiosInstance from 'src/utils/axios';

import { TCreatePackagingDamageDispatch } from 'src/redux/slices/packaging-damage-dispatch';

import Iconify from 'src/components/iconify';
import { RHFSelect, RHFTextField } from 'src/components/hook-form';

import { DAMAGE_REASON_OPTIONS } from 'src/types/damage-reason';
import { IPackagingItemListItem } from 'src/types/packaging-item';

interface Props {
  index: number;
  remove: (index: number) => void;
  isLast: boolean;
}

export default function PackagingDamageDispatchNewItemRow({
  index,
  remove,
  isLast,
}: Props) {
  const [itemSearchQuery, setItemSearchQuery] = useState('');
  const [localItems, setLocalItems] = useState<IPackagingItemListItem[]>([]);

  const {
    control,
    formState: { errors },
  } = useFormContext<TCreatePackagingDamageDispatch>();

  const defaultHelperText: string = errors.items?.[index] ? ' ' : '';

  // A (packagingItem, damageReason) pair already picked on another line
  // can't be picked again — the backend rejects that duplicate on submit
  // (should be one merged line instead). The same item CAN appear twice
  // with different reasons, so this only filters out the exact pair, not
  // the whole item — the reason field's own value is read live below.
  const allItems = useWatch({ control, name: 'items' });
  const currentDamageReason = useWatch({
    control,
    name: `items.${index}.damageReason` as any,
  });
  const selectedElsewhere = new Set(
    (allItems ?? [])
      .filter((_, i) => i !== index)
      .filter((item) => item?.damageReason === currentDamageReason)
      .map((item) => item?.packagingItem)
      .filter(Boolean)
  );
  const availableItems = localItems.filter(
    (item) => !selectedElsewhere.has(item._id)
  );

  const selectedItemId = useWatch({
    control,
    name: `items.${index}.packagingItem` as any,
  });
  const quantity = useWatch({
    control,
    name: `items.${index}.quantity` as any,
  });
  const selectedItem = localItems.find((item) => item._id === selectedItemId);

  const currentStockHelperText = selectedItem
    ? `Current stock: ${selectedItem.quantity} ${selectedItem.unit?.abbreviation ?? ''} → After damage: ${
        selectedItem.quantity - (Number(quantity) || 0)
      } ${selectedItem.unit?.abbreviation ?? ''}`
    : defaultHelperText;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchPaginatedPackagingItems = useCallback(
    debounce(async (query) => {
      try {
        const response = await axiosInstance.get(
          `packaging/item?page=&limit=&generalSearch=${query}&itemName=&itemCode=&minQuantity=&maxQuantity=&unit=&alertEnabled=&status=active&sortBy=itemName&sort=asc`
        );

        setLocalItems(response.data.data.items);
      } catch (err) {
        console.warn(err);
      }
    }, 500),
    []
  );

  useEffect(() => {
    fetchPaginatedPackagingItems(itemSearchQuery);

    return fetchPaginatedPackagingItems.cancel;
  }, [itemSearchQuery, fetchPaginatedPackagingItems]);

  return (
    <TableRow>
      <TableCell>
        {index + 1 < 10 ? `0${index + 1}` : index + 1}

        <FormHelperText>{defaultHelperText}</FormHelperText>
      </TableCell>

      <TableCell sx={{ minWidth: 280 }}>
        <Controller
          name={`items.${index}.packagingItem`}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Autocomplete
              {...field}
              fullWidth
              size="small"
              multiple={false}
              options={availableItems}
              isOptionEqualToValue={(
                option: IPackagingItemListItem | string,
                value: IPackagingItemListItem | string
              ) => {
                const optionId = typeof option === 'string' ? option : option?._id;
                const valueId = typeof value === 'string' ? value : value?._id;
                return optionId === valueId;
              }}
              getOptionLabel={(option: IPackagingItemListItem | string) => {
                if (!option) return '';
                if (typeof option === 'string') {
                  if (option.length === 0) return option;
                  const value = localItems.find(
                    (item: IPackagingItemListItem) => item._id === option
                  );
                  return value ? value.itemName : '';
                }
                return option.itemName;
              }}
              renderOption={(props, option: IPackagingItemListItem | string) => {
                if (typeof option === 'string') {
                  return (
                    <li {...props} key={option}>
                      {option}
                    </li>
                  );
                }
                return (
                  <li {...props} key={option._id}>
                    {option.itemName} ({option.itemCode}) — {option.quantity}{' '}
                    {option.unit?.abbreviation}
                  </li>
                );
              }}
              onInputChange={(_, value, reason) => {
                if (reason === 'input') setItemSearchQuery(value);
                if (reason === 'clear') setItemSearchQuery('');
              }}
              renderInput={(params) => (
                <TextField
                  error={!!error}
                  required
                  helperText={error ? error.message : defaultHelperText}
                  {...params}
                />
              )}
              onChange={(event, newValue) => {
                field.onChange(typeof newValue === 'string' ? newValue : newValue?._id ?? '');
              }}
            />
          )}
        />
      </TableCell>

      <TableCell sx={{ minWidth: 220 }}>
        <RHFTextField
          fullWidth
          required
          type="number"
          size="small"
          name={`items.${index}.quantity`}
          label="Quantity Damaged"
          helperText={currentStockHelperText}
          showError={false}
        />
      </TableCell>

      <TableCell sx={{ minWidth: 180 }}>
        <RHFSelect
          fullWidth
          required
          size="small"
          name={`items.${index}.damageReason`}
          label="Damage Reason"
        >
          {DAMAGE_REASON_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </RHFSelect>
      </TableCell>

      <TableCell align="right">
        <Tooltip title={isLast ? 'Last item cannot be deleted' : 'Delete'}>
          <span>
            <IconButton disabled={isLast} color="error" onClick={() => remove(index)}>
              <Iconify icon="solar:trash-bin-trash-bold" />
            </IconButton>
          </span>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
}
